import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { modalClasses } from '@mui/material';


const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

export default function ProjectsInfoModal({message} :any) {

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <span>
      <a href="" onClick={handleClickOpen}>{message}</a>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
          AI Agents, Projects and Insights
        </BootstrapDialogTitle>
        <DialogContent dividers>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{modalData}</ReactMarkdown>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </span>
  );
}

const modalData = `
## What Are Projects?
Projects help you organize and track AI-generated insights, research, and analysis. There are two powerful ways to use projects:

1. **Manual Projects**: Save and organize AI responses from your conversations
2. **Autonomous Projects**: Let AI Agents work independently on complex research projects for you.

## Autonomous AI Agents
Our AI Agents act as dedicated stock market research analysts that can tackle both quick complex tasks and long-running research projects. They work collaboratively to break down and execute projects while keeping you informed of important findings.

### What Can AI Agents Do?

#### Long-Term Monitoring
- Track specific stocks for important events and red flags
- Monitor local economic conditions in regions you're invested in
- Follow industry sectors and alert you by email to significant developments

#### Complex Analysis
- Process large amounts of financial data beyond the context window limits of AI Models.

### How It Works
1. **Project Creation**: Either:
   - Ask [Lima](chat) to start a monitoring project (e.g., "Monitor Apple stock for important events") or
   - Create a new [project](my_projects) manually with "autonomous project" enabled

2. **AI Planning**: Our AI agents will:
   - Plan and break down the project into manageable tasks
   - Execute the tasks and add insights to your project

3. **Updates**: For ongoing projects:
   - Agents perform daily monitoring
   - You receive email alerts for important findings that need your attention
   - All insights are saved to your project

### Project Types

#### Manual Projects
- Created and managed by you
- Perfect for organizing AI chat responses
- You control what gets saved and when.
- You save insights manually to the project from AI responses

#### Autonomous Projects
- **Long-term**: Continuous monitoring of entities, stocks, sectors, super investors, local economies, etc. until paused or deleted
- **Short-term**: One-time complex analysis tasks
- AI Agents handle task planning and execution of the project

## Getting Started
### Creating Projects
1. **For Autonomous Projects:**
   - Simply ask [Lima](chat) to start monitoring something e.g. "Monitor Apple stock for important events or red flags,",
    ![see here](/ai-agents/create_autonomous_projects_with_lima_chat_2.png)
   or
   - Create a new [project](my_projects) with "autonomous project" checked

2. **For Manual Projects:**
  - Click on an AI response and select "Save Insight to Project"
   ![Save Insight to Project Button](/projects-img/icon_button_save_insight_to_project.png)

### Managing Projects
- Access all projects from your Dashboard
![View My Projects Button](/projects-img/view_my_projects.png)
- View, edit, or delete projects and insights

### Add Projects to AI Chat
- Add projects to your AI chat with [Lima](chat) to provide additional context to the conversation. This adds all the insights from the project to the chat as a reference for Lima.
![Add Projects to Chat Button](/projects-img/add_projects_to_chat_icon_button.png)

### Best Practices
- Use autonomous projects for complex research or recurring/monitoring tasks
- Save important insights from conversations to manual projects for future reference
`
